// src/pages/Consultation/SessionDetail/Modals/OrderPlacementModal.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  FiX,
  FiTruck,
  FiMapPin,
  FiDollarSign,
  FiUser,
  FiPhone,
  FiMail,
  FiAlertCircle,
  FiCheckCircle,
  FiPackage,
  FiCalendar,
  FiClock,
  FiRefreshCw,
  FiCreditCard,
  FiSend,
  FiUserCheck,
} from "react-icons/fi";
import Button from "../../../components/common/Button";

const OrderPlacementModal = ({
  isOpen,
  onClose,
  onConfirm,
  session,
  customer,
  isAnonymous,
  summary,
  processing,
  primaryBranch,
}) => {
  const [config, setConfig] = useState({
    // Fulfillment
    deliveryType: "PICKUP",
    deliveryAddress: null,

    // Pickup type: "IMMEDIATE" or "SCHEDULED"
    pickupType: "SCHEDULED",

    // Pickup scheduling (for SCHEDULED pickup)
    pickupDate: "",
    pickupTimeSlot: "MORNING",
    pickupNotes: "",

    // Expected pickup person (for SCHEDULED - who will come to pick up)
    specifyExpectedPickupPerson: false,
    expectedPickupBy: {
      name: "",
      phone: "",
      relation: "FAMILY",
      idProof: "",
    },

    // Actual pickup person (for IMMEDIATE - who is picking up now)
    pickedUpBy: {
      name: "",
      phone: "",
      relation: "SELF",
      idProof: "",
    },

    // Payment
    paymentMethod: "CASH",

    // Payment collected status
    paymentCollected: false,
    paymentTransactionId: "",
    paymentNotes: "",

    // Refund settings
    refundMethod: "",
    bankDetails: {
      accountName: "",
      accountNumber: "",
      ifscCode: "",
      bankName: "",
      branchName: "",
    },
    upiDetails: {
      vpa: "",
      verifiedName: "",
    },
    refundNotes: "",

    // Anonymous order settings
    allowAnonymous: isAnonymous,
    requirePhone: true,
    requireEmail: false,

    // Customer info (for anonymous)
    customerName: "",
    customerPhone: "",
    customerEmail: "",

    // Notes
    notes: "",
  });

  const [errors, setErrors] = useState({});

  // Detect payment scenario
  const paymentScenario = useMemo(() => {
    const netPayable = summary?.netPayable || 0;

    if (netPayable < 0) {
      return {
        type: "CUSTOMER_REFUND",
        label: "Customer Receives Refund",
        refundAmount: Math.abs(netPayable),
        description: "Exchange value exceeds order total",
        color: "orange",
      };
    } else if (netPayable === 0) {
      return {
        type: "ZERO_PAYMENT",
        label: "No Payment Required",
        description: "Exchange value covers entire order",
        color: "green",
      };
    } else {
      return {
        type: "CUSTOMER_PAYS",
        label: "Customer Pays",
        amount: netPayable,
        description: "Normal payment required",
        color: "blue",
      };
    }
  }, [summary?.netPayable]);

  // Check if large refund (needs approval)
  const requiresRefundApproval = useMemo(() => {
    if (paymentScenario.type !== "CUSTOMER_REFUND") return false;
    const APPROVAL_THRESHOLD = 10000;
    return paymentScenario.refundAmount > APPROVAL_THRESHOLD;
  }, [paymentScenario]);

  // ===== BRANCH AVAILABILITY LOGIC =====
  const getDayOfWeek = (date) => {
    const days = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];
    return days[new Date(date).getDay()];
  };

  const isBranchOpenOnDate = (date) => {
    if (!primaryBranch?.settings?.workingHours) return false;
    const dayOfWeek = getDayOfWeek(date);
    const dayConfig = primaryBranch.settings.workingHours[dayOfWeek];
    return dayConfig?.isOpen === true;
  };

  const getBranchHoursForDate = (date) => {
    if (!primaryBranch?.settings?.workingHours) return null;
    const dayOfWeek = getDayOfWeek(date);
    const dayConfig = primaryBranch.settings.workingHours[dayOfWeek];
    if (!dayConfig?.isOpen) return null;
    return {
      open: dayConfig.open,
      close: dayConfig.close,
    };
  };

  const timeToMinutes = (timeStr) => {
    const [hours, minutes] = timeStr.split(":").map(Number);
    return hours * 60 + minutes;
  };

  const minutesToTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
  };

  const getBlockedPeriodsForDay = (date) => {
    if (!primaryBranch?.appointmentConfig?.schedulingRules?.blockedPeriods) {
      return [];
    }
    const dayOfWeek = getDayOfWeek(date);
    const blockedPeriods =
      primaryBranch.appointmentConfig.schedulingRules.blockedPeriods;
    return blockedPeriods.filter(
      (period) =>
        period.isActive &&
        period.daysOfWeek.includes(dayOfWeek) &&
        (period.recurring || period.specificDate === date)
    );
  };

  const isTimeRangeBlocked = (date, startTime, endTime) => {
    const blockedPeriods = getBlockedPeriodsForDay(date);
    const startMinutes = timeToMinutes(startTime);
    const endMinutes = timeToMinutes(endTime);
    return blockedPeriods.some((period) => {
      const blockStart = timeToMinutes(period.startTime);
      const blockEnd = timeToMinutes(period.endTime);
      return startMinutes < blockEnd && endMinutes > blockStart;
    });
  };

  const getAvailableTimeSlotsForDate = (date) => {
    const branchHours = getBranchHoursForDate(date);
    if (!branchHours) return [];

    const scheduleConfig = primaryBranch?.appointmentConfig?.schedulingRules;
    const startBuffer = scheduleConfig?.dayBuffers?.startBuffer?.enabled
      ? scheduleConfig.dayBuffers.startBuffer.minutes
      : 0;
    const endBuffer = scheduleConfig?.dayBuffers?.endBuffer?.enabled
      ? scheduleConfig.dayBuffers.endBuffer.minutes
      : 0;

    const effectiveStart = timeToMinutes(branchHours.open) + startBuffer;
    const effectiveEnd = timeToMinutes(branchHours.close) - endBuffer;

    const timeSlots = [
      {
        id: "MORNING",
        label: "Morning",
        start: effectiveStart,
        end: Math.min(effectiveStart + 180, effectiveEnd),
      },
      {
        id: "AFTERNOON",
        label: "Afternoon",
        start: effectiveStart + 180,
        end: Math.min(effectiveStart + 420, effectiveEnd),
      },
      {
        id: "EVENING",
        label: "Evening",
        start: effectiveStart + 420,
        end: effectiveEnd,
      },
    ];

    const now = new Date();
    const selectedDate = new Date(date);
    const isToday =
      selectedDate.toISOString().split("T")[0] ===
      now.toISOString().split("T")[0];
    const currentMinutes = isToday ? now.getHours() * 60 + now.getMinutes() : 0;

    return timeSlots
      .filter((slot) => {
        if (slot.start >= slot.end) return false;
        if (isToday && slot.end <= currentMinutes + 30) return false;
        const isCompletelyBlocked = isTimeRangeBlocked(
          date,
          minutesToTime(slot.start),
          minutesToTime(slot.end)
        );
        return !isCompletelyBlocked;
      })
      .map((slot) => {
        const blockedPeriods = getBlockedPeriodsForDay(date);
        const blockInfo = blockedPeriods.find((period) => {
          const blockStart = timeToMinutes(period.startTime);
          const blockEnd = timeToMinutes(period.endTime);
          return slot.start < blockEnd && slot.end > blockStart;
        });

        return {
          id: slot.id,
          label: slot.label,
          start: minutesToTime(slot.start),
          end: minutesToTime(slot.end),
          displayLabel: `${slot.label} (${minutesToTime(
            slot.start
          )} - ${minutesToTime(slot.end)})`,
          warning: blockInfo
            ? `Note: ${blockInfo.name} (${blockInfo.startTime}-${blockInfo.endTime})`
            : null,
        };
      });
  };

  const isDateValidForPickup = (date) => {
    const selectedDate = new Date(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (selectedDate < today) return false;
    if (!isBranchOpenOnDate(date)) return false;
    const availableSlots = getAvailableTimeSlotsForDate(date);
    return availableSlots.length > 0;
  };

  const getNextAvailableDate = () => {
    const today = new Date();
    let checkDate = new Date(today);
    checkDate.setDate(checkDate.getDate() + 1);
    for (let i = 0; i < 30; i++) {
      const dateStr = checkDate.toISOString().split("T")[0];
      if (isDateValidForPickup(dateStr)) {
        return dateStr;
      }
      checkDate.setDate(checkDate.getDate() + 1);
    }
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split("T")[0];
  };
  // ===== END BRANCH AVAILABILITY LOGIC =====

  // Initialize customer info and default pickup date
  useEffect(() => {
    if (isOpen) {
      if (isAnonymous && session?.guestCustomer) {
        setConfig((prev) => ({
          ...prev,
          customerName: session.guestCustomer.name || "",
          customerPhone: session.guestCustomer.phoneNumber || "",
          customerEmail: session.guestCustomer.email || "",
        }));
      } else if (customer) {
        setConfig((prev) => ({
          ...prev,
          customerName: customer.name || "",
          customerPhone: customer.phoneNumber || "",
          customerEmail: customer.email || "",
        }));
      }

      if (primaryBranch) {
        const nextAvailable = getNextAvailableDate();
        setConfig((prev) => ({
          ...prev,
          pickupDate: nextAvailable,
        }));
      }
    }
  }, [isOpen, isAnonymous, session, customer, primaryBranch]);

  const availableTimeSlots = useMemo(() => {
    if (!config.pickupDate || !primaryBranch) return [];
    return getAvailableTimeSlotsForDate(config.pickupDate);
  }, [config.pickupDate, primaryBranch]);

  useEffect(() => {
    if (availableTimeSlots.length > 0) {
      const isCurrentValid = availableTimeSlots.some(
        (slot) => slot.id === config.pickupTimeSlot
      );
      if (!isCurrentValid) {
        setConfig((prev) => ({
          ...prev,
          pickupTimeSlot: availableTimeSlots[0].id,
        }));
      }
    }
  }, [availableTimeSlots]);

  const validateForm = () => {
    const newErrors = {};

    // Anonymous customer validation
    if (isAnonymous) {
      if (!config.customerName || config.customerName.trim().length < 2) {
        newErrors.customerName = "Customer name is required (min 2 characters)";
      }

      if (config.requirePhone && !config.customerPhone) {
        newErrors.customerPhone = "Phone number is required";
      }

      if (config.customerPhone) {
        const phoneDigits = config.customerPhone.replace(/[^0-9]/g, "");
        if (!/^[6-9]\d{9}$/.test(phoneDigits)) {
          newErrors.customerPhone =
            "Invalid phone number (10 digits starting with 6-9)";
        }
      }

      if (config.requireEmail && !config.customerEmail) {
        newErrors.customerEmail = "Email is required";
      }

      if (
        config.customerEmail &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(config.customerEmail)
      ) {
        newErrors.customerEmail = "Invalid email format";
      }
    }

    // Delivery validation
    if (config.deliveryType === "DELIVERY" && !config.deliveryAddress) {
      newErrors.deliveryAddress = "Delivery address is required";
    }

    // Pickup validation
    if (config.deliveryType === "PICKUP") {
      // SCHEDULED pickup validation
      if (config.pickupType === "SCHEDULED") {
        if (!config.pickupDate) {
          newErrors.pickupDate = "Pickup date is required";
        } else if (!isDateValidForPickup(config.pickupDate)) {
          newErrors.pickupDate =
            "Branch is closed on this date or no slots available";
        }

        if (!config.pickupTimeSlot) {
          newErrors.pickupTimeSlot = "Please select a pickup time slot";
        } else if (availableTimeSlots.length > 0) {
          const isValidSlot = availableTimeSlots.some(
            (slot) => slot.id === config.pickupTimeSlot
          );
          if (!isValidSlot) {
            newErrors.pickupTimeSlot = "Selected time slot is not available";
          }
        }

        // Expected pickup person validation (optional)
        if (config.specifyExpectedPickupPerson) {
          if (
            !config.expectedPickupBy.name ||
            config.expectedPickupBy.name.trim().length < 2
          ) {
            newErrors.expectedPickupByName =
              "Expected pickup person name is required";
          }
          if (!config.expectedPickupBy.phone) {
            newErrors.expectedPickupByPhone =
              "Expected pickup person phone is required";
          } else {
            const phoneDigits = config.expectedPickupBy.phone.replace(
              /[^0-9]/g,
              ""
            );
            if (!/^[6-9]\d{9}$/.test(phoneDigits)) {
              newErrors.expectedPickupByPhone = "Invalid phone number";
            }
          }
        }
      }

      // IMMEDIATE pickup validation
      if (config.pickupType === "IMMEDIATE") {
        if (
          !config.pickedUpBy.name ||
          config.pickedUpBy.name.trim().length < 2
        ) {
          newErrors.pickedUpByName = "Name of person picking up is required";
        }
        if (!config.pickedUpBy.phone) {
          newErrors.pickedUpByPhone = "Phone number is required";
        } else {
          const phoneDigits = config.pickedUpBy.phone.replace(/[^0-9]/g, "");
          if (!/^[6-9]\d{9}$/.test(phoneDigits)) {
            newErrors.pickedUpByPhone = "Invalid phone number";
          }
        }
      }
    }

    // Payment collected validation
    if (config.paymentCollected && paymentScenario.type === "CUSTOMER_PAYS") {
      if (!config.paymentTransactionId && config.paymentMethod !== "CASH") {
        newErrors.paymentTransactionId =
          "Transaction ID is required for non-cash payments";
      }
    }

    // Refund validation
    if (paymentScenario.type === "CUSTOMER_REFUND") {
      if (!config.refundMethod) {
        newErrors.refundMethod = "Refund method is required";
      } else {
        if (config.refundMethod === "BANK_TRANSFER") {
          if (!config.bankDetails.accountName) {
            newErrors.bankAccountName = "Account holder name is required";
          }
          if (!config.bankDetails.accountNumber) {
            newErrors.bankAccountNumber = "Account number is required";
          }
          if (!config.bankDetails.ifscCode) {
            newErrors.bankIfscCode = "IFSC code is required";
          } else if (
            !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(config.bankDetails.ifscCode)
          ) {
            newErrors.bankIfscCode = "Invalid IFSC code format";
          }
          if (!config.bankDetails.bankName) {
            newErrors.bankName = "Bank name is required";
          }
        }

        if (config.refundMethod === "UPI") {
          if (!config.upiDetails.vpa) {
            newErrors.upiVpa = "UPI ID is required";
          } else if (!/^[\w.-]+@[\w.-]+$/.test(config.upiDetails.vpa)) {
            newErrors.upiVpa = "Invalid UPI ID format";
          }
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    const orderData = {
      // Fulfillment
      deliveryType: config.deliveryType,
      deliveryAddress: config.deliveryAddress,

      // Payment
      paymentMethod: config.paymentMethod,

      // Payment collected status
      paymentCollected: config.paymentCollected,
      ...(config.paymentCollected && {
        paymentTransactionId: config.paymentTransactionId,
        paymentNotes: config.paymentNotes,
      }),

      // Anonymous settings
      allowAnonymous: config.allowAnonymous,
      requirePhone: config.requirePhone,
      requireEmail: config.requireEmail,

      // Notes
      notes: config.notes,
    };

    // Add pickup details based on pickup type
    if (config.deliveryType === "PICKUP") {
      if (config.pickupType === "IMMEDIATE") {
        // IMMEDIATE PICKUP - Product is ready, picking up now
        orderData.immediatePickup = true;
        orderData.pickedUpBy = {
          name: config.pickedUpBy.name.trim(),
          phone: config.pickedUpBy.phone.trim(),
          relation: config.pickedUpBy.relation,
          idProof: config.pickedUpBy.idProof.trim(),
        };
        orderData.pickupNotes = config.pickupNotes;
      } else {
        // SCHEDULED PICKUP - Will pick up later
        orderData.immediatePickup = false;

        const selectedSlot = availableTimeSlots.find(
          (slot) => slot.id === config.pickupTimeSlot
        );

        orderData.pickupSchedule = {
          scheduledDate: config.pickupDate,
          scheduledTimeSlot: {
            start: selectedSlot?.start || "10:00",
            end: selectedSlot?.end || "22:00",
          },
          pickupNotes: config.pickupNotes || config.notes,
        };

        // Add expected pickup person if specified
        if (config.specifyExpectedPickupPerson) {
          orderData.expectedPickupBy = {
            name: config.expectedPickupBy.name.trim(),
            phone: config.expectedPickupBy.phone.trim(),
            relation: config.expectedPickupBy.relation,
            idProof: config.expectedPickupBy.idProof.trim(),
          };
        }
      }
    }

    // Add refund details if required
    if (paymentScenario.type === "CUSTOMER_REFUND") {
      orderData.refundMethod = config.refundMethod;

      if (config.refundMethod === "BANK_TRANSFER") {
        orderData.bankDetails = {
          accountName: config.bankDetails.accountName.trim(),
          accountNumber: config.bankDetails.accountNumber.trim(),
          ifscCode: config.bankDetails.ifscCode.trim().toUpperCase(),
          bankName: config.bankDetails.bankName.trim(),
          branchName: config.bankDetails.branchName?.trim() || "",
        };
      }

      if (config.refundMethod === "UPI") {
        orderData.upiDetails = {
          vpa: config.upiDetails.vpa.trim().toLowerCase(),
          verifiedName: config.upiDetails.verifiedName?.trim() || "",
        };
      }

      orderData.refundNotes = config.refundNotes;
    }

    // Add guest customer info if anonymous
    if (isAnonymous) {
      orderData.guestCustomer = {
        name: config.customerName.trim(),
        phoneNumber: config.customerPhone.trim(),
        email: config.customerEmail.trim() || undefined,
      };
    }

    onConfirm(orderData);
  };

  const handleChange = (field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handle nested field changes
  const handleNestedChange = (parent, field, value) => {
    setConfig((prev) => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value,
      },
    }));

    // Clear related error
    let errorKey;
    if (parent === "bankDetails") {
      errorKey = `bank${field.charAt(0).toUpperCase() + field.slice(1)}`;
    } else if (parent === "upiDetails") {
      errorKey = `upi${field.charAt(0).toUpperCase() + field.slice(1)}`;
    } else if (parent === "expectedPickupBy") {
      errorKey = `expectedPickupBy${
        field.charAt(0).toUpperCase() + field.slice(1)
      }`;
    } else if (parent === "pickedUpBy") {
      errorKey = `pickedUpBy${field.charAt(0).toUpperCase() + field.slice(1)}`;
    }

    if (errorKey && errors[errorKey]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  const handleDateChange = (e) => {
    const selectedDate = e.target.value;
    handleChange("pickupDate", selectedDate);
    if (!isDateValidForPickup(selectedDate)) {
      setErrors((prev) => ({
        ...prev,
        pickupDate: "Branch is closed on this date",
      }));
    }
  };

  // Auto-fill customer details for immediate pickup
  const fillCustomerAsPickupPerson = () => {
    const name = isAnonymous ? config.customerName : customer?.name || "";
    const phone = isAnonymous
      ? config.customerPhone
      : customer?.phoneNumber || "";

    setConfig((prev) => ({
      ...prev,
      pickedUpBy: {
        ...prev.pickedUpBy,
        name,
        phone,
        relation: "SELF",
      },
    }));
  };

  if (!isOpen) return null;

  const noBranchData = !primaryBranch;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={processing ? undefined : onClose}
      />

      {/* Modal */}
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
            <h2 className="text-xl font-semibold text-gray-900">Place Order</h2>
            {!processing && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX size={24} />
              </button>
            )}
          </div>

          {/* Content */}
          <div className="px-6 py-4 space-y-6">
            {/* No Branch Warning */}
            {noBranchData && config.deliveryType === "PICKUP" && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex gap-3">
                  <FiAlertCircle className="text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-red-800">
                    <p className="font-medium mb-1">Branch Data Missing</p>
                    <p>
                      Branch information is required for pickup scheduling.
                      Please contact support.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Payment Scenario Alert */}
            <div
              className={`rounded-lg p-4 border ${
                paymentScenario.type === "CUSTOMER_REFUND"
                  ? "bg-orange-50 border-orange-200"
                  : paymentScenario.type === "ZERO_PAYMENT"
                  ? "bg-green-50 border-green-200"
                  : "bg-blue-50 border-blue-200"
              }`}
            >
              <div className="flex gap-3">
                {paymentScenario.type === "CUSTOMER_REFUND" ? (
                  <FiRefreshCw className="flex-shrink-0 mt-0.5 text-orange-600" />
                ) : (
                  <FiCheckCircle
                    className={`flex-shrink-0 mt-0.5 ${
                      paymentScenario.type === "ZERO_PAYMENT"
                        ? "text-green-600"
                        : "text-blue-600"
                    }`}
                  />
                )}
                <div className="flex-1">
                  <p
                    className={`font-semibold text-sm ${
                      paymentScenario.type === "CUSTOMER_REFUND"
                        ? "text-orange-800"
                        : paymentScenario.type === "ZERO_PAYMENT"
                        ? "text-green-800"
                        : "text-blue-800"
                    }`}
                  >
                    {paymentScenario.label}
                  </p>
                  <p
                    className={`text-xs mt-1 ${
                      paymentScenario.type === "CUSTOMER_REFUND"
                        ? "text-orange-700"
                        : paymentScenario.type === "ZERO_PAYMENT"
                        ? "text-green-700"
                        : "text-blue-700"
                    }`}
                  >
                    {paymentScenario.description}
                  </p>
                  {paymentScenario.type === "CUSTOMER_REFUND" && (
                    <>
                      <p className="text-lg font-bold mt-2 text-orange-600">
                        Refund Amount: ₹
                        {paymentScenario.refundAmount.toLocaleString()}
                      </p>
                      {requiresRefundApproval && (
                        <p className="text-xs mt-1 text-orange-600 font-medium">
                          ⚠️ Manager approval required (amount &gt; ₹10,000)
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Order Summary Preview */}
            <div className="bg-primary/5 rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <FiPackage className="text-primary" />
                Order Summary
              </h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-600">Items:</span>
                  <span className="ml-2 font-medium">
                    {session?.outcome?.itemsAddedToCart || 0}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Exchange Items:</span>
                  <span className="ml-2 font-medium">
                    {session?.exchangeItems?.filter((e) => e.customerAccepted)
                      .length || 0}
                  </span>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-600">Order Total:</span>
                  <span className="ml-2 font-medium">
                    ₹{(summary?.grandTotal || 0).toLocaleString()}
                  </span>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-600">Exchange Value:</span>
                  <span className="ml-2 font-medium text-green-600">
                    -₹{(summary?.totalExchangeValue || 0).toLocaleString()}
                  </span>
                </div>
                <div className="col-span-2 pt-2 border-t border-primary/20">
                  <span className="text-gray-600">
                    {paymentScenario.type === "CUSTOMER_REFUND"
                      ? "Refund Due:"
                      : "Net Payable:"}
                  </span>
                  <span
                    className={`ml-2 font-bold text-lg ${
                      paymentScenario.type === "CUSTOMER_REFUND"
                        ? "text-orange-600"
                        : "text-primary"
                    }`}
                  >
                    ₹{Math.abs(summary?.netPayable || 0).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            {/* Customer Information (for Anonymous) */}
            {isAnonymous && (
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  <FiUser className="text-primary" />
                  Customer Information
                </h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={config.customerName}
                    onChange={(e) =>
                      handleChange("customerName", e.target.value)
                    }
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${
                      errors.customerName ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder="Enter customer name"
                    disabled={processing}
                  />
                  {errors.customerName && (
                    <p className="text-xs text-red-600 mt-1">
                      {errors.customerName}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number{" "}
                    {config.requirePhone && (
                      <span className="text-red-500">*</span>
                    )}
                  </label>
                  <div className="relative">
                    <FiPhone className="absolute left-3 top-3 text-gray-400" />
                    <input
                      type="tel"
                      value={config.customerPhone}
                      onChange={(e) =>
                        handleChange("customerPhone", e.target.value)
                      }
                      className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${
                        errors.customerPhone
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                      placeholder="10-digit phone number"
                      disabled={processing}
                    />
                  </div>
                  {errors.customerPhone && (
                    <p className="text-xs text-red-600 mt-1">
                      {errors.customerPhone}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email{" "}
                    {config.requireEmail && (
                      <span className="text-red-500">*</span>
                    )}
                  </label>
                  <div className="relative">
                    <FiMail className="absolute left-3 top-3 text-gray-400" />
                    <input
                      type="email"
                      value={config.customerEmail}
                      onChange={(e) =>
                        handleChange("customerEmail", e.target.value)
                      }
                      className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${
                        errors.customerEmail
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                      placeholder="customer@example.com"
                      disabled={processing}
                    />
                  </div>
                  {errors.customerEmail && (
                    <p className="text-xs text-red-600 mt-1">
                      {errors.customerEmail}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Registered Customer Info (Read-only) */}
            {!isAnonymous && customer && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <FiUser className="text-primary" />
                  Customer Details
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Name:</span>
                    <span className="font-medium">{customer.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Phone:</span>
                    <span className="font-medium">{customer.phoneNumber}</span>
                  </div>
                  {customer.email && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Email:</span>
                      <span className="font-medium">{customer.email}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Refund Details Section */}
            {paymentScenario.type === "CUSTOMER_REFUND" && (
              <div className="space-y-4 border-2 border-orange-200 rounded-lg p-4 bg-orange-50">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  <FiRefreshCw className="text-orange-600" />
                  Refund Details
                </h3>

                <div className="bg-white rounded-lg p-3 text-sm text-orange-800">
                  <p>
                    Customer will receive{" "}
                    <strong>
                      ₹{paymentScenario.refundAmount.toLocaleString()}
                    </strong>{" "}
                    back because exchange value exceeds the order total.
                  </p>
                </div>

                {/* Refund Method Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Refund Method <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      {
                        id: "BANK_TRANSFER",
                        label: "Bank Transfer",
                        icon: FiCreditCard,
                      },
                      { id: "UPI", label: "UPI", icon: FiSend },
                      { id: "CASH", label: "Cash", icon: FiDollarSign },
                      {
                        id: "STORE_CREDIT",
                        label: "Store Credit",
                        icon: FiPackage,
                      },
                    ].map((method) => (
                      <button
                        key={method.id}
                        type="button"
                        onClick={() => handleChange("refundMethod", method.id)}
                        disabled={processing}
                        className={`p-3 border-2 rounded-lg text-center transition-all ${
                          config.refundMethod === method.id
                            ? "border-orange-600 bg-orange-100"
                            : "border-gray-300 hover:border-gray-400 bg-white"
                        }`}
                      >
                        <method.icon
                          className={`mx-auto mb-1 text-xl ${
                            config.refundMethod === method.id
                              ? "text-orange-600"
                              : "text-gray-400"
                          }`}
                        />
                        <p className="text-sm font-medium">{method.label}</p>
                      </button>
                    ))}
                  </div>
                  {errors.refundMethod && (
                    <p className="text-xs text-red-600 mt-1">
                      {errors.refundMethod}
                    </p>
                  )}
                </div>

                {/* Bank Transfer Details */}
                {config.refundMethod === "BANK_TRANSFER" && (
                  <div className="space-y-3 bg-white rounded-lg p-4 border border-orange-200">
                    <h4 className="text-sm font-semibold text-gray-800">
                      Bank Account Details
                    </h4>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Account Holder Name{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={config.bankDetails.accountName}
                        onChange={(e) =>
                          handleNestedChange(
                            "bankDetails",
                            "accountName",
                            e.target.value
                          )
                        }
                        className={`w-full px-3 py-2 border rounded-lg text-sm ${
                          errors.bankAccountName
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                        placeholder="As per bank records"
                        disabled={processing}
                      />
                      {errors.bankAccountName && (
                        <p className="text-xs text-red-600 mt-1">
                          {errors.bankAccountName}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Account Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={config.bankDetails.accountNumber}
                        onChange={(e) =>
                          handleNestedChange(
                            "bankDetails",
                            "accountNumber",
                            e.target.value
                          )
                        }
                        className={`w-full px-3 py-2 border rounded-lg text-sm ${
                          errors.bankAccountNumber
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                        placeholder="Enter account number"
                        disabled={processing}
                      />
                      {errors.bankAccountNumber && (
                        <p className="text-xs text-red-600 mt-1">
                          {errors.bankAccountNumber}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        IFSC Code <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={config.bankDetails.ifscCode}
                        onChange={(e) =>
                          handleNestedChange(
                            "bankDetails",
                            "ifscCode",
                            e.target.value.toUpperCase()
                          )
                        }
                        className={`w-full px-3 py-2 border rounded-lg text-sm uppercase ${
                          errors.bankIfscCode
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                        placeholder="HDFC0001234"
                        disabled={processing}
                        maxLength={11}
                      />
                      {errors.bankIfscCode && (
                        <p className="text-xs text-red-600 mt-1">
                          {errors.bankIfscCode}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Bank Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={config.bankDetails.bankName}
                        onChange={(e) =>
                          handleNestedChange(
                            "bankDetails",
                            "bankName",
                            e.target.value
                          )
                        }
                        className={`w-full px-3 py-2 border rounded-lg text-sm ${
                          errors.bankName ? "border-red-500" : "border-gray-300"
                        }`}
                        placeholder="HDFC Bank"
                        disabled={processing}
                      />
                      {errors.bankName && (
                        <p className="text-xs text-red-600 mt-1">
                          {errors.bankName}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Branch Name (Optional)
                      </label>
                      <input
                        type="text"
                        value={config.bankDetails.branchName}
                        onChange={(e) =>
                          handleNestedChange(
                            "bankDetails",
                            "branchName",
                            e.target.value
                          )
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="Mumbai Main Branch"
                        disabled={processing}
                      />
                    </div>
                  </div>
                )}

                {/* UPI Details */}
                {config.refundMethod === "UPI" && (
                  <div className="space-y-3 bg-white rounded-lg p-4 border border-orange-200">
                    <h4 className="text-sm font-semibold text-gray-800">
                      UPI Details
                    </h4>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        UPI ID <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={config.upiDetails.vpa}
                        onChange={(e) =>
                          handleNestedChange(
                            "upiDetails",
                            "vpa",
                            e.target.value.toLowerCase()
                          )
                        }
                        className={`w-full px-3 py-2 border rounded-lg text-sm lowercase ${
                          errors.upiVpa ? "border-red-500" : "border-gray-300"
                        }`}
                        placeholder="customer@paytm"
                        disabled={processing}
                      />
                      {errors.upiVpa && (
                        <p className="text-xs text-red-600 mt-1">
                          {errors.upiVpa}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Name (Optional)
                      </label>
                      <input
                        type="text"
                        value={config.upiDetails.verifiedName}
                        onChange={(e) =>
                          handleNestedChange(
                            "upiDetails",
                            "verifiedName",
                            e.target.value
                          )
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="As per UPI"
                        disabled={processing}
                      />
                    </div>
                  </div>
                )}

                {/* Refund Notes */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Refund Notes (Optional)
                  </label>
                  <textarea
                    value={config.refundNotes}
                    onChange={(e) =>
                      handleChange("refundNotes", e.target.value)
                    }
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="Any additional notes about the refund"
                    disabled={processing}
                  />
                </div>
              </div>
            )}

            {/* Fulfillment Options */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <FiTruck className="text-primary" />
                Fulfillment Method
              </h3>

              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => handleChange("deliveryType", "PICKUP")}
                  disabled={processing || noBranchData}
                  className={`p-4 border-2 rounded-lg text-center transition-all ${
                    config.deliveryType === "PICKUP"
                      ? "border-primary bg-primary/5"
                      : "border-gray-200 hover:border-gray-300"
                  } ${noBranchData ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <FiMapPin
                    className={`mx-auto mb-2 text-2xl ${
                      config.deliveryType === "PICKUP"
                        ? "text-primary"
                        : "text-gray-400"
                    }`}
                  />
                  <p className="font-medium">Store Pickup</p>
                  <p className="text-xs text-gray-600 mt-1">
                    Customer collects from store
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => handleChange("deliveryType", "DELIVERY")}
                  disabled={processing}
                  className={`p-4 border-2 rounded-lg text-center transition-all ${
                    config.deliveryType === "DELIVERY"
                      ? "border-primary bg-primary/5"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <FiTruck
                    className={`mx-auto mb-2 text-2xl ${
                      config.deliveryType === "DELIVERY"
                        ? "text-primary"
                        : "text-gray-400"
                    }`}
                  />
                  <p className="font-medium">Home Delivery</p>
                  <p className="text-xs text-gray-600 mt-1">
                    Deliver to customer
                  </p>
                </button>
              </div>

              {/* Pickup Options */}
              {config.deliveryType === "PICKUP" && !noBranchData && (
                <div className="space-y-4">
                  {/* Pickup Type Selection */}
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Pickup Type
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => handleChange("pickupType", "IMMEDIATE")}
                        disabled={processing}
                        className={`p-3 border-2 rounded-lg text-center transition-all ${
                          config.pickupType === "IMMEDIATE"
                            ? "border-green-600 bg-green-50"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <FiCheckCircle
                          className={`mx-auto mb-1 text-xl ${
                            config.pickupType === "IMMEDIATE"
                              ? "text-green-600"
                              : "text-gray-400"
                          }`}
                        />
                        <p className="text-sm font-medium">Immediate Pickup</p>
                        <p className="text-xs text-gray-600 mt-1">
                          Product ready, picking up now
                        </p>
                      </button>

                      <button
                        type="button"
                        onClick={() => handleChange("pickupType", "SCHEDULED")}
                        disabled={processing}
                        className={`p-3 border-2 rounded-lg text-center transition-all ${
                          config.pickupType === "SCHEDULED"
                            ? "border-blue-600 bg-blue-50"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <FiCalendar
                          className={`mx-auto mb-1 text-xl ${
                            config.pickupType === "SCHEDULED"
                              ? "text-blue-600"
                              : "text-gray-400"
                          }`}
                        />
                        <p className="text-sm font-medium">Scheduled Pickup</p>
                        <p className="text-xs text-gray-600 mt-1">
                          Will pick up on a future date
                        </p>
                      </button>
                    </div>
                  </div>

                  {/* IMMEDIATE PICKUP - Who is picking up NOW */}
                  {config.pickupType === "IMMEDIATE" && (
                    <div className="space-y-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-gray-800 flex items-center gap-2">
                          <FiUserCheck className="text-green-600" />
                          Who is Picking Up Now?
                        </h4>
                        <button
                          type="button"
                          onClick={fillCustomerAsPickupPerson}
                          className="text-xs text-green-600 hover:text-green-700 underline"
                          disabled={processing}
                        >
                          Fill with Customer Details
                        </button>
                      </div>

                      <div className="bg-white rounded-lg p-4 border border-green-200 space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={config.pickedUpBy.name}
                            onChange={(e) =>
                              handleNestedChange(
                                "pickedUpBy",
                                "name",
                                e.target.value
                              )
                            }
                            className={`w-full px-3 py-2 border rounded-lg text-sm ${
                              errors.pickedUpByName
                                ? "border-red-500"
                                : "border-gray-300"
                            }`}
                            placeholder="Full name of person picking up"
                            disabled={processing}
                          />
                          {errors.pickedUpByName && (
                            <p className="text-xs text-red-600 mt-1">
                              {errors.pickedUpByName}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Phone Number <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="tel"
                            value={config.pickedUpBy.phone}
                            onChange={(e) =>
                              handleNestedChange(
                                "pickedUpBy",
                                "phone",
                                e.target.value
                              )
                            }
                            className={`w-full px-3 py-2 border rounded-lg text-sm ${
                              errors.pickedUpByPhone
                                ? "border-red-500"
                                : "border-gray-300"
                            }`}
                            placeholder="10-digit number"
                            disabled={processing}
                          />
                          {errors.pickedUpByPhone && (
                            <p className="text-xs text-red-600 mt-1">
                              {errors.pickedUpByPhone}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Relation to Customer
                          </label>
                          <select
                            value={config.pickedUpBy.relation}
                            onChange={(e) =>
                              handleNestedChange(
                                "pickedUpBy",
                                "relation",
                                e.target.value
                              )
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            disabled={processing}
                          >
                            <option value="SELF">Self (Customer)</option>
                            <option value="FAMILY">Family Member</option>
                            <option value="FRIEND">Friend</option>
                            <option value="COLLEAGUE">Colleague</option>
                            <option value="AUTHORIZED_REPRESENTATIVE">
                              Authorized Representative
                            </option>
                            <option value="OTHER">Other</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            ID Proof Shown (Optional)
                          </label>
                          <input
                            type="text"
                            value={config.pickedUpBy.idProof}
                            onChange={(e) =>
                              handleNestedChange(
                                "pickedUpBy",
                                "idProof",
                                e.target.value
                              )
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            placeholder="Aadhar Card / PAN / Driving License"
                            disabled={processing}
                          />
                        </div>
                      </div>

                      {/* Pickup Notes for Immediate */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Pickup Notes (Optional)
                        </label>
                        <textarea
                          value={config.pickupNotes}
                          onChange={(e) =>
                            handleChange("pickupNotes", e.target.value)
                          }
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          placeholder="Any notes about this pickup"
                          disabled={processing}
                        />
                      </div>
                    </div>
                  )}

                  {/* SCHEDULED PICKUP - When and who will come */}
                  {config.pickupType === "SCHEDULED" && (
                    <div className="space-y-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <h4 className="font-medium text-gray-800 flex items-center gap-2">
                        <FiCalendar className="text-blue-600" />
                        Schedule Pickup
                      </h4>

                      {/* Branch Info */}
                      {primaryBranch && (
                        <div className="bg-white rounded p-3 text-sm">
                          <p className="font-medium text-gray-800">
                            {primaryBranch.name}
                          </p>
                          <p className="text-gray-600 text-xs mt-1">
                            {primaryBranch.address.line1},{" "}
                            {primaryBranch.address.city}
                          </p>
                        </div>
                      )}

                      {/* Pickup Date */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Pickup Date <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          value={config.pickupDate}
                          onChange={handleDateChange}
                          min={new Date().toISOString().split("T")[0]}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${
                            errors.pickupDate
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                          disabled={processing}
                        />
                        {errors.pickupDate && (
                          <p className="text-xs text-red-600 mt-1">
                            {errors.pickupDate}
                          </p>
                        )}
                        {config.pickupDate && !errors.pickupDate && (
                          <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                            <FiCheckCircle size={12} />
                            Branch is open on this date
                          </p>
                        )}
                      </div>

                      {/* Pickup Time Slot */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Pickup Time Slot{" "}
                          <span className="text-red-500">*</span>
                        </label>

                        {availableTimeSlots.length === 0 ? (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                            <p className="text-sm text-yellow-800">
                              No time slots available for the selected date.
                              Please choose another date.
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {availableTimeSlots.map((slot) => (
                              <button
                                key={slot.id}
                                type="button"
                                onClick={() =>
                                  handleChange("pickupTimeSlot", slot.id)
                                }
                                disabled={processing}
                                className={`w-full p-3 border-2 rounded-lg text-left transition-all ${
                                  config.pickupTimeSlot === slot.id
                                    ? "border-primary bg-primary/5"
                                    : "border-gray-200 hover:border-gray-300"
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <FiClock
                                      className={
                                        config.pickupTimeSlot === slot.id
                                          ? "text-primary"
                                          : "text-gray-400"
                                      }
                                    />
                                    <div>
                                      <p className="font-medium">
                                        {slot.displayLabel}
                                      </p>
                                      {slot.warning && (
                                        <p className="text-xs text-orange-600 mt-1">
                                          {slot.warning}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  {config.pickupTimeSlot === slot.id && (
                                    <FiCheckCircle className="text-primary" />
                                  )}
                                </div>
                              </button>
                            ))}
                          </div>
                        )}

                        {errors.pickupTimeSlot && (
                          <p className="text-xs text-red-600 mt-1">
                            {errors.pickupTimeSlot}
                          </p>
                        )}
                      </div>

                      {/* Expected Pickup Person (Optional) */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <label className="block text-sm font-medium text-gray-700">
                            Specify Expected Pickup Person?
                          </label>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={config.specifyExpectedPickupPerson}
                              onChange={(e) =>
                                handleChange(
                                  "specifyExpectedPickupPerson",
                                  e.target.checked
                                )
                              }
                              disabled={processing}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                          </label>
                        </div>

                        {config.specifyExpectedPickupPerson && (
                          <div className="bg-white rounded-lg p-4 border border-blue-200 space-y-3">
                            <h5 className="text-sm font-semibold text-gray-800">
                              Expected Pickup Person Details
                            </h5>

                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Name <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                value={config.expectedPickupBy.name}
                                onChange={(e) =>
                                  handleNestedChange(
                                    "expectedPickupBy",
                                    "name",
                                    e.target.value
                                  )
                                }
                                className={`w-full px-3 py-2 border rounded-lg text-sm ${
                                  errors.expectedPickupByName
                                    ? "border-red-500"
                                    : "border-gray-300"
                                }`}
                                placeholder="Who will come to pick up"
                                disabled={processing}
                              />
                              {errors.expectedPickupByName && (
                                <p className="text-xs text-red-600 mt-1">
                                  {errors.expectedPickupByName}
                                </p>
                              )}
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Phone Number{" "}
                                <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="tel"
                                value={config.expectedPickupBy.phone}
                                onChange={(e) =>
                                  handleNestedChange(
                                    "expectedPickupBy",
                                    "phone",
                                    e.target.value
                                  )
                                }
                                className={`w-full px-3 py-2 border rounded-lg text-sm ${
                                  errors.expectedPickupByPhone
                                    ? "border-red-500"
                                    : "border-gray-300"
                                }`}
                                placeholder="10-digit number"
                                disabled={processing}
                              />
                              {errors.expectedPickupByPhone && (
                                <p className="text-xs text-red-600 mt-1">
                                  {errors.expectedPickupByPhone}
                                </p>
                              )}
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Relation
                              </label>
                              <select
                                value={config.expectedPickupBy.relation}
                                onChange={(e) =>
                                  handleNestedChange(
                                    "expectedPickupBy",
                                    "relation",
                                    e.target.value
                                  )
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                disabled={processing}
                              >
                                <option value="SELF">Self (Customer)</option>
                                <option value="FAMILY">Family Member</option>
                                <option value="FRIEND">Friend</option>
                                <option value="COLLEAGUE">Colleague</option>
                                <option value="AUTHORIZED_REPRESENTATIVE">
                                  Authorized Representative
                                </option>
                                <option value="OTHER">Other</option>
                              </select>
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                ID Proof to be Shown (Optional)
                              </label>
                              <input
                                type="text"
                                value={config.expectedPickupBy.idProof}
                                onChange={(e) =>
                                  handleNestedChange(
                                    "expectedPickupBy",
                                    "idProof",
                                    e.target.value
                                  )
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                placeholder="Aadhar Card / PAN / Driving License"
                                disabled={processing}
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Pickup Notes */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Pickup Instructions (Optional)
                        </label>
                        <textarea
                          value={config.pickupNotes}
                          onChange={(e) =>
                            handleChange("pickupNotes", e.target.value)
                          }
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                          placeholder="Any special instructions for pickup"
                          disabled={processing}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Delivery Address */}
              {config.deliveryType === "DELIVERY" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Delivery Address <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={config.deliveryAddress || ""}
                    onChange={(e) =>
                      handleChange("deliveryAddress", e.target.value)
                    }
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${
                      errors.deliveryAddress
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    placeholder="Enter complete delivery address"
                    disabled={processing}
                  />
                  {errors.deliveryAddress && (
                    <p className="text-xs text-red-600 mt-1">
                      {errors.deliveryAddress}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Payment Method */}
            {paymentScenario.type === "CUSTOMER_PAYS" && (
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  <FiDollarSign className="text-primary" />
                  Payment Details
                </h3>

                {/* Payment Method Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Method
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {["CASH", "CARD", "UPI"].map((method) => (
                      <button
                        key={method}
                        type="button"
                        onClick={() => handleChange("paymentMethod", method)}
                        disabled={processing}
                        className={`p-3 border-2 rounded-lg text-center transition-all ${
                          config.paymentMethod === method
                            ? "border-primary bg-primary/5"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <p className="font-medium">{method}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Payment Collected Toggle */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-800">
                        Payment Already Collected?
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        Mark if payment was received before placing order
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={config.paymentCollected}
                        onChange={(e) =>
                          handleChange("paymentCollected", e.target.checked)
                        }
                        disabled={processing}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                    </label>
                  </div>

                  {/* Payment Details */}
                  {config.paymentCollected && (
                    <div className="mt-4 space-y-3">
                      <div className="flex items-center gap-2 text-green-700">
                        <FiCheckCircle />
                        <span className="text-sm font-medium">
                          Payment of ₹{paymentScenario.amount.toLocaleString()}{" "}
                          collected
                        </span>
                      </div>

                      {config.paymentMethod !== "CASH" && (
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Transaction ID{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={config.paymentTransactionId}
                            onChange={(e) =>
                              handleChange(
                                "paymentTransactionId",
                                e.target.value
                              )
                            }
                            className={`w-full px-3 py-2 border rounded-lg text-sm ${
                              errors.paymentTransactionId
                                ? "border-red-500"
                                : "border-gray-300"
                            }`}
                            placeholder="Enter transaction/reference ID"
                            disabled={processing}
                          />
                          {errors.paymentTransactionId && (
                            <p className="text-xs text-red-600 mt-1">
                              {errors.paymentTransactionId}
                            </p>
                          )}
                        </div>
                      )}

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Payment Notes (Optional)
                        </label>
                        <input
                          type="text"
                          value={config.paymentNotes}
                          onChange={(e) =>
                            handleChange("paymentNotes", e.target.value)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          placeholder="Any notes about the payment"
                          disabled={processing}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Order Notes */}
            {config.deliveryType !== "PICKUP" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Order Notes (Optional)
                </label>
                <textarea
                  value={config.notes}
                  onChange={(e) => handleChange("notes", e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="Any special instructions or notes"
                  disabled={processing}
                />
              </div>
            )}

            {/* Confirmation Notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex gap-3">
                <FiAlertCircle className="text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Confirm Order Placement</p>
                  <p>
                    By placing this order, you confirm that all details are
                    correct and the customer has agreed to the terms.
                    {config.pickupType === "IMMEDIATE" && (
                      <strong>
                        {" "}
                        The order will be marked as DELIVERED immediately.
                      </strong>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex gap-3 z-10">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={processing}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={processing}
              className="flex-1"
            >
              {processing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  <FiCheckCircle className="mr-2" />
                  {config.pickupType === "IMMEDIATE"
                    ? "Complete Pickup & Place Order"
                    : "Confirm & Place Order"}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderPlacementModal;
